import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Wordmark } from "@/components/Wordmark";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import { EnvironmentSaveStudy, STUDY_VARIANTS, type StudyId } from "./EnvironmentSaveStudies";
import styles from "./environment-save.module.css";

const LEGACY_VARIANTS = ["ledger", "stage", "manifest"] as const;
const VARIANTS = [...LEGACY_VARIANTS, ...STUDY_VARIANTS] as const;
const PHASES = ["running", "saving", "archived", "restoring", "attention"] as const;

type VariantId = (typeof VARIANTS)[number];
type LegacyId = (typeof LEGACY_VARIANTS)[number];
type PhaseId = (typeof PHASES)[number];

function isLegacy(variant: VariantId): variant is LegacyId {
  return (LEGACY_VARIANTS as readonly string[]).includes(variant);
}

function readChoice<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

const SAMPLE_POINTS = [
  { id: "sample-1", when: "2026-10-18 09:40 UTC" },
  { id: "sample-2", when: "2026-10-22 16:05 UTC" },
] as const;

export function EnvironmentSaveVariantsPage() {
  const { m, setLocale } = useLocale();
  const copy = m.environmentSave;
  const [params, setParams] = useSearchParams();
  const lang = params.get("lang");

  useEffect(() => {
    if (lang === "en" || lang === "de") setLocale(lang);
  }, [lang, setLocale]);
  const variant = readChoice(params.get("variant"), VARIANTS, "ledger");
  const phase = readChoice(params.get("phase"), PHASES, "archived");

  function setChoice(key: "variant" | "phase", value: string) {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  }

  return (
    <div>
      <p className={styles.banner}>
        <strong>{copy.bannerMark}</strong> {copy.banner}
      </p>
      <div className={styles.page} data-measure={isLegacy(variant) ? undefined : "study"}>
        <header className={styles.top}>
          <Wordmark to="/" />
          <LanguageSwitch />
        </header>
        <p className={styles.lead}>{copy.lead}</p>
        <div className={styles.controls}>
          <div className={styles.control}>
            <span id="save-variant-label">{copy.variantLabel}</span>
            <div className={styles.variantGroup} role="radiogroup" aria-labelledby="save-variant-label">
              <span className={styles.groupLabel}>{copy.studies.earlier}</span>
              {LEGACY_VARIANTS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={variant === id}
                  onClick={() => setChoice("variant", id)}
                >
                  {copy.variants[id].name}
                </button>
              ))}
              <span className={styles.groupLabel}>{copy.studies.newer}</span>
              {STUDY_VARIANTS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={variant === id}
                  onClick={() => setChoice("variant", id)}
                >
                  {copy.variants[id].name}
                </button>
              ))}
            </div>
          </div>
          <label className={styles.control}>
            <span>{copy.phaseLabel}</span>
            <select
              className={ui.input}
              value={phase}
              onChange={(event) => setChoice("phase", event.target.value)}
            >
              {PHASES.map((id) => (
                <option key={id} value={id}>
                  {copy.phases[id].name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {isLegacy(variant) ? (
          <div className={styles.frame} data-variant={variant} data-phase={phase}>
            <aside className={styles.rail}>
              <span className={styles.railCurrent}>{m.nav.environment}</span>
              <p>{copy.railNote}</p>
            </aside>
            <div className={styles.main}>
              <p className={styles.kicker}>{copy.kicker}</p>
              <h1>{copy.variants[variant].name}</h1>
              <p className={styles.blurb}>{copy.variants[variant].blurb}</p>
              <VariantBody variant={variant} phase={phase} />
              <p className={ui.meta} id="save-not-connected">
                {copy.notConnected}
              </p>
              <Actions />
              <p className={styles.consequence}>{copy.deleteConsequence}</p>
              <p className={styles.consequence}>{copy.bootNote}</p>
              <div className={styles.actions}>
                <ButtonLink to="/app/environment" variant="secondary">
                  {copy.back}
                </ButtonLink>
              </div>
            </div>
          </div>
        ) : (
          <EnvironmentSaveStudy variant={variant as StudyId} phase={phase} />
        )}
      </div>
    </div>
  );
}

function Facts() {
  const { m } = useLocale();
  const copy = m.environmentSave;
  return (
    <div className={styles.facts}>
      <p className={styles.fact}>
        <b>{copy.lastSaveTitle}</b>
        <span>{copy.lastSaveNone}</span>
      </p>
      <p className={styles.fact}>
        <b>{copy.sizeTitle}</b>
        <span>{copy.sizeUnknown}</span>
      </p>
      <p className={styles.fact}>
        <b>{copy.retentionTitle}</b>
        <span>{copy.retentionBody}</span>
      </p>
      <p className={styles.fact}>
        <b>{copy.quotaTitle}</b>
        <span>{copy.quotaBody}</span>
      </p>
      <p className={styles.fact}>
        <b>{copy.compatibilityTitle}</b>
        <span>{copy.compatibilityBody}</span>
      </p>
      <p className={styles.fact}>
        <b>{copy.icloudTitle}</b>
        <span>{copy.icloudBody}</span>
      </p>
    </div>
  );
}

function Points() {
  const { m } = useLocale();
  const copy = m.environmentSave;
  return (
    <section>
      <h2 className={styles.kicker}>{copy.pointsTitle}</h2>
      <ul className={styles.points}>
        {SAMPLE_POINTS.map((point) => (
          <li key={point.id}>
            <strong>
              {copy.pointSample} · {point.when}
            </strong>
            <span className={ui.meta}>{copy.pointUnverified}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PhaseNotice({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  return (
    <div className={styles.notice} role="status">
      <strong>{copy.phases[phase].name}. </strong>
      {copy.phases[phase].body} {copy.macReady}
      {phase === "saving" || phase === "restoring" ? ` ${copy.progressSteps}` : null}
    </div>
  );
}

function VariantBody({ variant, phase }: { variant: LegacyId; phase: PhaseId }) {
  if (variant === "stage") return <Stage phase={phase} />;
  if (variant === "manifest") return <Manifest phase={phase} />;
  return <Ledger phase={phase} />;
}

function Ledger({ phase }: { phase: PhaseId }) {
  return (
    <>
      <PhaseNotice phase={phase} />
      <Facts />
      <Points />
    </>
  );
}

function Stage({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  return (
    <div className={styles.stage}>
      <div>
        <p className={styles.stageWord}>{copy.phases[phase].name}</p>
        <p className={styles.stageBody}>{copy.phases[phase].body}</p>
        <p className={styles.stageBody}>{copy.macReady}</p>
        {phase === "saving" || phase === "restoring" ? (
          <p className={styles.stageBody}>{copy.progressSteps}</p>
        ) : null}
        <Points />
      </div>
      <div>
        <Facts />
      </div>
    </div>
  );
}

function Manifest({ phase }: { phase: PhaseId }) {
  const { m } = useLocale();
  const copy = m.environmentSave;
  const groups = [
    ["included", copy.included, copy.includedItems],
    ["excluded", copy.excluded, copy.excludedItems],
    ["cloud", copy.cloudOnly, copy.cloudItems],
    ["blocked", copy.nonRestorable, copy.nonRestorableItems],
  ] as const;
  return (
    <>
      <PhaseNotice phase={phase} />
      <Facts />
      <div className={styles.manifest}>
        {groups.map(([key, title, items]) => (
          <section key={key} className={styles.group}>
            <h2>{title}</h2>
            <ul>
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <Points />
    </>
  );
}

function Actions() {
  const { m } = useLocale();
  const copy = m.environmentSave;
  return (
    <div className={styles.actions}>
      <Button variant="primary" disabled aria-describedby="save-not-connected">
        {copy.saveRelease}
      </Button>
      <Button variant="secondary" disabled aria-describedby="save-not-connected">
        {copy.restore}
      </Button>
      <Button variant="secondary" disabled aria-describedby="save-not-connected">
        {copy.exportAction}
      </Button>
      <Button variant="danger" disabled aria-describedby="save-not-connected">
        {copy.deleteAction}
      </Button>
    </div>
  );
}
